import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { FlatList, Pressable, RefreshControl, SectionList, Text, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useColorScheme } from 'nativewind';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useFocusEffect } from '@react-navigation/core';
import Layout from '../../components/Layout';
import {
  HelpdeskIssueFilter,
  HelpdeskIssueSection,
  HelpdeskSectionHeader,
  Loader,
} from '../../components';
import client from '../../client/client';
import analytics from '../../utils/analytics';
import { HelpdeskIssueSearchResult } from '../../client/interfaces';
import { useMessage } from '../../contexts/Messages/MessageContext';
import { useHelpdesk } from '../../contexts/HelpdeskContext/HelpdeskContext';
import {
  NavigationParamList,
  ScreenName,
  StackName,
  StackNavigationScreenProps,
} from '../../navigation/types';
import { NavigationContextStack } from '../../contexts/NavigationContext/NavigationContextStack';
import { isMobile, isNative, isTablet, navHeight, isWeb, isIos } from '../../utils/responsive';
import { cln } from '../../utils/classnames';

const HelpdeskScreen: React.FC<
  StackNavigationScreenProps<NavigationParamList, ScreenName.HelpdeskScreen>
> = ({ navigation, route }) => {
  const { t } = useTranslation();
  const { setMessage } = useMessage();
  const { colorScheme } = useColorScheme();
  const insets = useSafeAreaInsets();

  const { setStackNavigation, setStackRoute, stackRoute, changedStack, setChangedStack } =
    useContext(NavigationContextStack);

  const {
    params,
    setParams,
    topics,
    organizations,
    totalCount,
    setTotalCount,
    resetFilters,
    emptyParams,
    refreshTopicsAndOrganizations,
  } = useHelpdesk();

  const [results, setResults] = useState<HelpdeskIssueSearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Set<number>>(new Set());
  const [needsRefresh, setNeedsRefresh] = useState(true);
  const lastLoggedSearch = useRef<string | null>(null);

  const emptyHelpdeskState = () => {
    setIsLoading(true);
    setParams({ ...emptyParams });
    setResults([]);
    setExpandedSections(new Set());
  };

  useEffect(() => {
    if (stackRoute && changedStack === StackName.HelpdeskStack) {
      if (!isMobile || (isMobile && stackRoute?.name === 'helpdesk')) {
        emptyHelpdeskState();
        isMobile && fetchHelpdeskIssues(emptyParams);
      }
      setChangedStack(null);
    }
  }, [changedStack, stackRoute]);

  useFocusEffect(
    useCallback(() => {
      setStackRoute(route);
      setStackNavigation(navigation);
      setNeedsRefresh(false);
    }, [changedStack]),
  );

  // Track when leaving to another stack (mobile only)
  useEffect(() => {
    if (!isMobile) return;
    const unsubscribe = navigation.getParent()?.addListener('blur', () => {
      setNeedsRefresh(true);
    });
    return unsubscribe;
  }, [navigation]);

  const toggleSection = (topicId: number) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(topicId)) next.delete(topicId);
      else next.add(topicId);
      return next;
    });
  };

  const isFilterActive = !!(
    params.search ||
    params.topics?.length >= 1 ||
    params.organizations?.length >= 1
  );

  // Show loader immediately if returning from another stack (prevents flash of stale data)
  const isResetPending = isMobile && needsRefresh;

  const fetchHelpdeskIssues = async (fetchParams = params) => {
    try {
      setIsLoading(true);
      const data = await client.getHelpdeskIssues({
        search: fetchParams.search,
        topicIdList: fetchParams.topics?.map((tp) => tp.id),
        organizationIdList: fetchParams.organizations?.map((o) => o.id),
      });
      const totalResults = data.reduce((sum, r) => sum + r.helpdeskIssues.length, 0);
      setResults(data);
      setTotalCount(totalResults);

      if (fetchParams.search) {
        if (lastLoggedSearch.current !== fetchParams.search) {
          lastLoggedSearch.current = fetchParams.search;
          await analytics().logEvent('search', {
            search_term: fetchParams.search,
            content_type: 'subtopic',
            results_count: totalResults,
            has_results: totalResults > 0 ? 1 : 0,
          });
        }
      } else {
        lastLoggedSearch.current = null;
      }
    } catch (error) {
      setMessage({ message: error.message, type: 'error' });
    }
    setIsLoading(false);
  };

  useEffect(() => {
    fetchHelpdeskIssues(params);
  }, [JSON.stringify(params)]);

  useFocusEffect(
    useCallback(() => {
      if (!isMobile) {
        setResults([]);
        refreshTopicsAndOrganizations();
        fetchHelpdeskIssues(params);
      }
    }, []),
  );

  const onRefresh = useCallback(() => {
    setRefreshing(true);
    fetchHelpdeskIssues();
    setTimeout(() => {
      setRefreshing(false);
    }, 800);
  }, []);

  const refreshControl = (
    <RefreshControl
      refreshing={refreshing}
      onRefresh={onRefresh}
      progressViewOffset={-10}
      tintColor={colorScheme === 'dark' ? '#b0b0b0' : '#d1d1d1'}
      colors={colorScheme === 'dark' ? ['#b0b0b0'] : ['#f6f6f6']}
      progressBackgroundColor={
        colorScheme === 'dark' ? 'rgba(79,79,79,0.9)' : 'rgba(136,136,136,0.9)'
      }
    />
  );

  const contentContainerStyle = { paddingTop: isFilterActive ? 0 : 16 };
  const paddingTopNative = isIos ? navHeight + insets.top + 8 : navHeight + insets.top + 16;

  const mobileFeed =
    (isLoading && results.length === 0) || isResetPending ? (
      <View className="flex-1 items-center justify-center">
        <Loader />
      </View>
    ) : isNative ? (
      <View
        style={{
          flex: 1,
          overflow: 'hidden',
          paddingTop: isFilterActive ? 0 : insets.top - 12,
        }}
        testID="helpdesk-screen-native"
      >
        <SectionList
          sections={results.map((r) => ({ ...r, data: [r] }))}
          keyExtractor={(item) => String(item.topicId)}
          renderSectionHeader={({ section }) =>
            section.helpdeskIssues.length > 2 && expandedSections.has(section.topicId) ? (
              <HelpdeskSectionHeader item={section} searchText={params.search} />
            ) : null
          }
          style={{ marginTop: isFilterActive ? -8 : 24 }}
          renderItem={({ item, section }) => (
            <>
              {!(section.helpdeskIssues.length > 2 && expandedSections.has(section.topicId)) && (
                <HelpdeskSectionHeader item={section} searchText={params.search} />
              )}
              <HelpdeskIssueSection
                item={item}
                searchText={params.search}
                hideHeader
                showAll={expandedSections.has(item.topicId)}
                onToggleShowAll={() => toggleSection(item.topicId)}
              />
            </>
          )}
          stickySectionHeadersEnabled
          refreshControl={refreshControl}
          contentContainerStyle={contentContainerStyle}
        />
        {isLoading && (
          <View className="items-center justify-center py-5">
            <Loader />
          </View>
        )}
      </View>
    ) : (
      <View testID="helpdesk-screen-mobile" style={{ flex: 1, overflow: 'hidden' }}>
        {isLoading && (
          <View className="items-center justify-center py-5">
            <Loader />
          </View>
        )}
        <FlatList
          data={results}
          keyExtractor={(item) => String(item.topicId)}
          renderItem={({ item }) => <HelpdeskIssueSection item={item} searchText={params.search} />}
          refreshControl={refreshControl}
          contentContainerStyle={contentContainerStyle}
        />
      </View>
    );

  const desktopFeed = (
    <View testID="helpdesk-screen-web-desktop">
      {isLoading && results.length === 0 ? (
        <View className="items-center justify-center py-10">
          <Loader />
        </View>
      ) : (
        results.map((item) => (
          <HelpdeskIssueSection key={item.topicId} item={item} searchText={params.search} />
        ))
      )}
      {isLoading && results.length > 0 && (
        <View className="items-center justify-center py-10">
          <Loader />
        </View>
      )}
    </View>
  );

  return (
    <Layout
      title={!isMobile && t('helpdesk:title')}
      isScrollView={!isMobile}
      titleMarginBottom={false}
      isListScreen={true}
    >
      {isFilterActive && !isTablet && isMobile ? (
        <View
          className="flex-row self-end px-[30px]"
          style={{
            paddingTop: isNative ? paddingTopNative : 56,
          }}
        >
          <Text className="text-neutral-950 dark:text-neutral-50 text-body font-[sans-700] mr-1.5">
            {totalCount}
          </Text>
          <Text className="text-neutral-950 dark:text-neutral-50 text-body font-[sans-400]">
            {t('articles:results')}
          </Text>
        </View>
      ) : (
        <></>
      )}

      {isFilterActive && !isTablet && isMobile ? (
        <View className="px-[30px]">
          <Pressable
            className="justify-center items-center"
            onPress={resetFilters}
            style={{ alignSelf: 'flex-end' }}
          >
            <Text className="text-primary-600 dark:text-primary-500 text-body font-[sans-400] py-2">
              {t('articles:remove_search')}
            </Text>
          </Pressable>
        </View>
      ) : (
        <></>
      )}

      {isMobile && !isFilterActive && <View className="mt-10" />}
      {!isMobile && (
        <View className="mt-10 z-50">
          <HelpdeskIssueFilter
            params={params}
            paramSetter={setParams}
            showStatus={false}
            totalCount={results.reduce((sum, r) => sum + r.helpdeskIssues.length, 0)}
            topics={topics}
            organizations={organizations}
            clearOnBlur={false}
          />
        </View>
      )}

      {results.length === 0 ? (
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        <>
          {isLoading && !isMobile && (
            <View className="items-center justify-center py-10">
              <Loader />
            </View>
          )}
          {!isLoading && (
            <View
              className={cln(
                'flex-column px-[32px] mt-4',
                isMobile && !isFilterActive && 'pt-[120px]',
              )}
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              style={{ display: isWeb ? 'inline' : 'flex' }}
            >
              <Text className="text-h3 md:text-h2 text-primaryLight-500 dark:text-primary-500 font-[sans-600]">
                {t('articles:no_results')}{' '}
              </Text>
              <Text className="text-h3 md:text-h2 text-neutral-950 dark:text-neutral-50 font-[sans-600]">
                {t('articles:based_on_your_search')}.
              </Text>
            </View>
          )}
        </>
      ) : isMobile ? (
        mobileFeed
      ) : (
        desktopFeed
      )}
    </Layout>
  );
};

export default HelpdeskScreen;
